import React, { useRef, useEffect, useCallback } from 'react';
import './RichEditor.css';

// Toolbar button definitions
const TOOLBAR = [
  { cmd: 'bold',          icon: '<b>B</b>',          title: 'Bold' },
  { cmd: 'italic',        icon: '<i>I</i>',           title: 'Italic' },
  { cmd: 'underline',     icon: '<u>U</u>',           title: 'Underline' },
  { cmd: 'strikeThrough', icon: '<s>S</s>',           title: 'Strikethrough' },
  { type: 'sep' },
  { cmd: 'formatBlock', value: 'h1', icon: 'H1', title: 'Heading 1' },
  { cmd: 'formatBlock', value: 'h2', icon: 'H2', title: 'Heading 2' },
  { cmd: 'formatBlock', value: 'h3', icon: 'H3', title: 'Heading 3' },
  { cmd: 'formatBlock', value: 'p',  icon: 'P',  title: 'Paragraph' },
  { type: 'sep' },
  { cmd: 'insertUnorderedList', icon: '• List',  title: 'Bullet List' },
  { cmd: 'insertOrderedList',   icon: '1. List', title: 'Numbered List' },
  { type: 'sep' },
  { cmd: 'justifyLeft',   icon: '⬅',  title: 'Align Left' },
  { cmd: 'justifyCenter', icon: '☰',  title: 'Align Center' },
  { cmd: 'justifyRight',  icon: '➡',  title: 'Align Right' },
  { type: 'sep' },
  { type: 'color',  cmd: 'foreColor',  icon: 'A',  title: 'Text Color' },
  { type: 'color',  cmd: 'hiliteColor', icon: '▌', title: 'Highlight' },
  { type: 'sep' },
  { cmd: 'removeFormat', icon: '✕ fmt', title: 'Clear Formatting' },
];

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

export default function RichEditor({ value, onChange, placeholder = 'Start typing...' }) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);
  // Save selection before color picker steals focus
  const savedRange = useRef(null);

  // Sync external value → editor (only when value changes externally)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (isInternalChange.current) { isInternalChange.current = false; return; }
    // Only update DOM if content actually differs to avoid cursor jump
    if (el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    isInternalChange.current = true;
    onChange(el.innerHTML);
  }, [onChange]);

  const execCmd = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    emitChange();
  }, [emitChange]);

  const handleColorChange = useCallback((cmd, color) => {
    const editor = editorRef.current;
    if (!editor) return;

    // Restore the selection we saved before the color picker opened
    editor.focus();
    const sel = window.getSelection();
    sel.removeAllRanges();

    let range;
    if (savedRange.current) {
      range = savedRange.current;
      sel.addRange(range);
    } else {
      // No saved range — place cursor at end of editor
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel.addRange(range);
    }

    const span = document.createElement('span');

    if (cmd === 'foreColor') {
      span.style.color = color;
    } else {
      span.style.backgroundColor = color;
    }

    if (range.collapsed) {
      // Nothing selected — insert zero-width space so next typed text inherits color
      span.appendChild(document.createTextNode('\u200B'));
      range.insertNode(span);
      // Place cursor inside the span (after the zero-width char)
      const newRange = document.createRange();
      newRange.setStart(span.firstChild, 1);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    } else {
      // Text is selected — wrap it in the colored span
      span.appendChild(range.extractContents());
      range.insertNode(span);
      // Move cursor to end of inserted span
      const newRange = document.createRange();
      newRange.setStartAfter(span);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }

    emitChange();
  }, [emitChange]);

  const handleFontSize = useCallback((e) => {
    const size = e.target.value;
    editorRef.current?.focus();
    // execCommand fontSize only accepts 1-7, so use inline style instead
    document.execCommand('fontSize', false, '7');
    const fontEls = editorRef.current.querySelectorAll('font[size="7"]');
    fontEls.forEach(el => {
      el.removeAttribute('size');
      el.style.fontSize = size;
    });
    emitChange();
  }, [emitChange]);

  return (
    <div className="re-wrapper">
      {/* Toolbar */}
      <div className="re-toolbar">
        {TOOLBAR.map((btn, i) => {
          if (btn.type === 'sep') return <div key={i} className="re-sep" />;

          if (btn.type === 'color') {
            return (
              <label
                key={i}
                className="re-color-btn"
                title={btn.title}
                onMouseDown={() => {
                  // Save selection NOW — before the color input steals focus
                  const sel = window.getSelection();
                  if (sel && sel.rangeCount > 0) {
                    savedRange.current = sel.getRangeAt(0).cloneRange();
                  } else {
                    savedRange.current = null;
                  }
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: btn.icon }} />
                <input
                  type="color"
                  defaultValue={btn.cmd === 'foreColor' ? '#e53e3e' : '#fef08a'}
                  onInput={e => handleColorChange(btn.cmd, e.target.value)}
                  onChange={e => handleColorChange(btn.cmd, e.target.value)}
                  className="re-color-input"
                />
              </label>
            );
          }

          return (
            <button
              key={i}
              type="button"
              className="re-btn"
              title={btn.title}
              onMouseDown={e => {
                e.preventDefault(); // prevent editor blur
                execCmd(btn.cmd, btn.value || null);
              }}
              dangerouslySetInnerHTML={{ __html: btn.icon }}
            />
          );
        })}

        {/* Font size selector */}
        <select className="re-fontsize" onChange={handleFontSize} defaultValue="" title="Font Size">
          <option value="" disabled>Size</option>
          {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        className="re-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        data-placeholder={placeholder}
      />
    </div>
  );
}
